import React, { FC } from 'react';
import { Alert, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
	CHECKLIST_CHECK_TYPES,
	CHECKLIST_CONFIDENCE_DISPLAY_NAMES,
	CHECKLIST_CONFIDENCE_LEVELS,
	CHECKLIST_DOCUMENT_TYPE_DISPLAY_NAMES,
	CHECKLIST_ITEM_SEVERITIES,
	CHECKLIST_MATCH_MODE_DISPLAY_NAMES,
	ChecklistExtraction,
	ChecklistExtractionItem,
} from '../../types/ComplianceChecklistTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { formatCurrency } from '../../utils/DeliverableUtils';

// The model is told to leave these tokens in expected values instead of the
// actual name; read them as prose rather than as a literal.
const EXPECTED_TEXT_TOKEN_LABELS = {
	'{{supplier.legal_name}}': "the supplier's legal name on file",
	'{{customer.legal_name}}': "your company's legal name",
	'{{customer.address}}': "your company's address",
};

// Bracket access, not a nullSafeGet path — the tokens themselves contain periods.
const describeExpectedText = (expectedText: string): string =>
	EXPECTED_TEXT_TOKEN_LABELS[expectedText] || `"${expectedText}"`;

// One plain-language line describing how a requirement will be checked against a
// supplier's document. Exported so the evaluation results view can render the
// same "required:" phrasing the buyer saw when the checklist was created.
export const describeChecklistRequirement = (item: ChecklistExtractionItem): string => {
	const matchMode = nullSafeGet('match_mode', item);
	const matchModeSuffix = matchMode ? ` — ${CHECKLIST_MATCH_MODE_DISPLAY_NAMES[matchMode]}` : '';
	const expectedText = nullSafeGet('expected_text', item);
	const expectedNumber = nullSafeGet('expected_number', item);
	const expectedOptions = nullSafeGetOrElse('expected_options', item, []);
	switch (item.check_type) {
		case CHECKLIST_CHECK_TYPES.PRESENT:
			return 'Must be filled in';
		case CHECKLIST_CHECK_TYPES.ABSENT:
			return 'Must be left blank';
		case CHECKLIST_CHECK_TYPES.EQUALS:
			return `Must be ${describeExpectedText(expectedText)}${matchModeSuffix}`;
		case CHECKLIST_CHECK_TYPES.CONTAINS:
			return `Must mention ${describeExpectedText(expectedText)}${matchModeSuffix}`;
		case CHECKLIST_CHECK_TYPES.ONE_OF:
			return `Must be one of: ${expectedOptions.join(', ')}`;
		case CHECKLIST_CHECK_TYPES.MIN_AMOUNT:
			return `At least ${formatCurrency(expectedNumber)}`;
		case CHECKLIST_CHECK_TYPES.MAX_AMOUNT:
			return `No more than ${formatCurrency(expectedNumber)}`;
		case CHECKLIST_CHECK_TYPES.CHECKBOX_CHECKED:
			return 'Box must be checked';
		case CHECKLIST_CHECK_TYPES.CHECKBOX_UNCHECKED:
			return 'Box must be left unchecked';
		case CHECKLIST_CHECK_TYPES.DATE_PRESENT:
			return 'Date must be filled in';
		case CHECKLIST_CHECK_TYPES.DATE_IN_EFFECT:
			return 'Must already be in effect when the document is reviewed';
		case CHECKLIST_CHECK_TYPES.DATE_NOT_EXPIRED:
			return 'Must not be expired when the document is reviewed';
		case CHECKLIST_CHECK_TYPES.SIGNATURE_PRESENT:
			return 'Signature must appear';
		default:
			return nullSafeGetOrElse('field_path', item, '');
	}
};

interface ChecklistSection {
	name: string;
	items: ChecklistExtractionItem[];
}

// Sections in the order they first appear on the form (items arrive ordered by
// display_order, which is form order).
const groupItemsBySection = (items: ChecklistExtractionItem[]): ChecklistSection[] => {
	const sections: ChecklistSection[] = [];
	const sectionsByName: { [sectionName: string]: ChecklistSection } = {};
	items.forEach((item) => {
		const sectionName = nullSafeGetOrElse('section', item, 'Other');
		if (!sectionsByName[sectionName]) {
			sectionsByName[sectionName] = { name: sectionName, items: [] };
			sections.push(sectionsByName[sectionName]);
		}
		sectionsByName[sectionName].items.push(item);
	});
	return sections;
};

const sortByDisplayOrder = (items: ChecklistExtractionItem[]) =>
	[...items].sort(
		(firstItem, secondItem) =>
			nullSafeGetOrElse('display_order', firstItem, 0) -
			nullSafeGetOrElse('display_order', secondItem, 0)
	);

interface ComplianceChecklistReviewProps {
	extraction: ChecklistExtraction;
	// Name of the uploaded sample these requirements were read from.
	fileName?: string;
}

// Read-only view of the checklist extracted from a buyer's sample document,
// grouped by the section of the source form.
export const ComplianceChecklistReview: FC<ComplianceChecklistReviewProps> = ({
	extraction,
	fileName,
}): React.ReactElement => {
	const items = sortByDisplayOrder(nullSafeGetOrElse('items', extraction, []));
	const sections = groupItemsBySection(items);
	const documentType = nullSafeGet('document_type', extraction);
	const documentTypeName = CHECKLIST_DOCUMENT_TYPE_DISPLAY_NAMES[documentType] || documentType;
	const formIdentifier = nullSafeGet('form_identifier', extraction);
	const needsCheckCount = items.filter(
		(item) => item.confidence !== CHECKLIST_CONFIDENCE_LEVELS.HIGH
	).length;

	const renderItemTags = (item: ChecklistExtractionItem) => {
		const isRecommended = item.severity === CHECKLIST_ITEM_SEVERITIES.RECOMMENDED;
		const isHighConfidence = item.confidence === CHECKLIST_CONFIDENCE_LEVELS.HIGH;
		return (
			<div className="flex shrink-0 flex-row items-center gap-1">
				{isRecommended ? <Tag color="blue">Recommended</Tag> : null}
				{isHighConfidence ? null : (
					<Tooltip title={nullSafeGet('rationale', item)}>
						<Tag color="gold">
							{CHECKLIST_CONFIDENCE_DISPLAY_NAMES[item.confidence] || 'Please check'}
						</Tag>
					</Tooltip>
				)}
			</div>
		);
	};

	const renderItem = (item: ChecklistExtractionItem) => (
		<div
			key={item.key}
			className="flex flex-row items-start justify-between gap-4 border-0 border-b border-solid border-grey-4 py-3 last:border-b-0"
		>
			<div className="flex flex-col gap-1">
				<span className="bodymedium">{item.label}</span>
				<span className="footnoteregular text-character-secondary">
					{describeChecklistRequirement(item)}
				</span>
				{nullSafeGet('evidence', item) ? (
					<span className="footnoteregular text-grey-7">From your sample: {item.evidence}</span>
				) : null}
			</div>
			{renderItemTags(item)}
		</div>
	);

	const renderSection = (section: ChecklistSection) => (
		<div key={section.name} className="flex flex-col">
			<div className="flex flex-row items-center justify-between gap-2 bg-grey-3 px-4 py-2">
				<span className="bodybold">{section.name}</span>
				<span className="footnoteregular text-character-secondary">
					{section.items.length} {section.items.length === 1 ? 'requirement' : 'requirements'}
				</span>
			</div>
			<div className="px-4">{section.items.map(renderItem)}</div>
		</div>
	);

	return (
		<div className="flex flex-col gap-4">
			<Alert
				type="info"
				showIcon={true}
				icon={<InfoCircleOutlined />}
				message={`${items.length} ${
					items.length === 1 ? 'requirement was' : 'requirements were'
				} read from your sample`}
				description="Supplier documents are checked against these requirements."
			/>
			<div className="footnoteregular flex flex-row flex-wrap gap-x-6 gap-y-1 text-character-secondary">
				<span>Document type: {documentTypeName}</span>
				{formIdentifier ? <span>Form: {formIdentifier}</span> : null}
				{fileName ? <span>Sample: {fileName}</span> : null}
				{needsCheckCount > 0 ? <span>{needsCheckCount} worth a second look</span> : null}
			</div>
			<div className="flex flex-col overflow-hidden rounded border border-solid border-grey-4">
				{sections.map(renderSection)}
			</div>
		</div>
	);
};
